/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-core: numeric helpers (core-viewer.js 2417-2428, 3166-3183, 3504-3532).
// valuesForWindow takes the recording duration explicitly instead of reading D.duration.

export function mean(arr) {
  return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0
}

export function median(arr) {
  if (!arr.length) return 0
  const s = [...arr].sort((a, b) => a - b)
  const m = Math.floor(s.length / 2)
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2
}

export function sd(arr) {
  if (arr.length < 2) return 0
  const mu = mean(arr)
  return Math.sqrt(mean(arr.map((x) => (x - mu) * (x - mu))))
}

export function percentile(values, p) {
  if (!values.length) return 0
  const sorted = values.slice().sort((a, b) => a - b)
  const idx = Math.max(0, Math.min(sorted.length - 1, p * (sorted.length - 1)))
  const lo = Math.floor(idx),
    hi = Math.ceil(idx)
  if (lo === hi) return sorted[lo]
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo)
}

export function normalizedSuccessiveDifferencePct(values) {
  if (!values || values.length < 2) return null
  const m = mean(values)
  if (!m) return null
  const diffs = []
  for (let i = 1; i < values.length; i++) diffs.push(values[i] - values[i - 1])
  return (100 * Math.sqrt(mean(diffs.map((v) => v * v)))) / m
}

export function lagAutocorrelation(values, lag) {
  if (!values || values.length <= lag + 2) return null
  const m = mean(values)
  const centered = values.map((v) => v - m)
  const denom = centered.reduce((acc, v) => acc + v * v, 0)
  if (!denom) return null
  let num = 0
  for (let i = lag; i < centered.length; i++) num += centered[i] * centered[i - lag]
  return num / denom
}

export function valuesForWindow(trace, start, end, duration) {
  if (!trace || !trace.length || end <= start || !duration) return []
  const i0 = Math.max(0, Math.floor((start / duration) * trace.length))
  const i1 = Math.min(trace.length, Math.ceil((end / duration) * trace.length))
  const out = []
  for (let i = i0; i < i1; i++) out.push(trace[i])
  return out
}

export function peakCount(values, threshold) {
  if (values.length < 3) return values.some((v) => v >= threshold) ? 1 : 0
  let count = 0
  for (let i = 1; i < values.length - 1; i++) {
    if (values[i] >= threshold && values[i] >= values[i - 1] && values[i] > values[i + 1]) count += 1
  }
  return count
}

export function sustainedSeconds(values, threshold, duration) {
  if (!values.length || duration <= 0) return 0
  const above = values.filter((v) => v >= threshold).length
  return (duration * above) / values.length
}
